package org.example.zone360.components

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.css.BoxSizing
import com.varabyte.kobweb.compose.css.CSSLengthOrPercentageNumericValue
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.css.Transition
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.alignItems
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.boxSizing
import com.varabyte.kobweb.compose.ui.modifiers.cursor
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.justifyContent
import com.varabyte.kobweb.compose.ui.modifiers.left
import com.varabyte.kobweb.compose.ui.modifiers.onClick
import com.varabyte.kobweb.compose.ui.modifiers.opacity
import com.varabyte.kobweb.compose.ui.modifiers.overflow
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.position
import com.varabyte.kobweb.compose.ui.modifiers.size
import com.varabyte.kobweb.compose.ui.modifiers.top
import com.varabyte.kobweb.compose.ui.modifiers.transform
import com.varabyte.kobweb.compose.ui.modifiers.transition
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.text.SpanText
import org.example.zone360.theme.SitePalette
import org.example.zone360.utils.Res
import org.jetbrains.compose.web.css.AlignItems
import org.jetbrains.compose.web.css.Color
import org.jetbrains.compose.web.css.JustifyContent
import org.jetbrains.compose.web.css.Position
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.s
import org.jetbrains.compose.web.dom.Div

@Composable
fun CustomPagination(
    currentPage: Int,
    totalPages: Int,
    onPageChange: (Int) -> Unit,
    sitePalette: SitePalette,
    leftRightPadding: CSSLengthOrPercentageNumericValue = 0.px,
    topBottomPadding: CSSLengthOrPercentageNumericValue = 10.px,
) {
    val dotSize = 10.px
    val dotGap = 10.px
    val visibleDots = 3
    val wrapperWidth = 55.px // 50px + a little breathing room to avoid right cut

    Column(
        modifier = Modifier
            .fillMaxWidth()
            .alignItems(AlignItems.Center)
            .padding(topBottom = topBottomPadding, leftRight = leftRightPadding)
    ) {
        Row(
            modifier = Modifier
                .width(100.percent)
                .gap(10.px)
                .alignItems(AlignItems.Center)
                .justifyContent(JustifyContent.SpaceBetween)
        ) {
            // Left: Page Count
            SpanText(
                text = "${currentPage + 1}/$totalPages",
                modifier = Modifier.fontSize(16.px).fontWeight(500)
            )

            // Center: Dots
            Box(
                modifier = Modifier
                    .size(width = wrapperWidth, height = 30.px)
                    .overflow(Overflow.Hidden)
                    .position(Position.Relative)
                    .boxSizing(BoxSizing.BorderBox),
                contentAlignment = Alignment.Center
            ) {
                Row(
                    modifier = Modifier
                        .position(Position.Absolute)
                        .align(Alignment.Center)
                        .top(50.percent)
                        .left(0.px)
                        .transform {
                            translateY((-50).percent)

                            val offset = when {
                                currentPage <= 1 -> 0
                                currentPage >= totalPages - 2 -> (totalPages - visibleDots) * (10 + 10)
                                else -> (currentPage - 1) * (10 + 10)
                            }
                            translateX((-offset).px)
                        }
                        .transition(Transition.of("transform", 0.5.s))
                        .gap(dotGap)
                ) {
                    (0 until totalPages).forEach { index ->
                        Div(
                            attrs = Modifier
                                .size(dotSize)
                                .backgroundColor(
                                    if (index == currentPage) Color("#555")
                                    else Color("#bbb")
                                )
                                .borderRadius(50.percent)
                                .cursor(Cursor.Pointer)
                                .transition(Transition.of("all", 0.4.s))
                                .onClick { onPageChange(index) }
                                .transform {
                                    when (index) {
                                        currentPage -> scale(1.2)
                                        (maxOf(0, minOf(currentPage - 1, totalPages - 3))) + 2 -> scale(0.6)
                                    }
                                }
                                .opacity(
                                    if (index == (maxOf(0, minOf(currentPage - 1, totalPages - 3))) + 2) 0.6 else 1.0
                                )
                                .toAttrs {
                                    if (index == currentPage && index == 0) {
                                        style {
                                            property("transform-origin", "left center")
                                        }
                                    }
                                }
                        ) {}
                    }
                }
            }

            // Right: Arrows
            Row(Modifier.gap(8.px)) {
                Image(
                    src = Res.Images.arrow_Left,
                    modifier = Modifier
                        .cursor(Cursor.Pointer)
                        .onClick {
                            if (currentPage > 0) {
                                onPageChange(currentPage - 1)
                            }
                        }
                )
                Image(
                    src = Res.Images.arrow_Right,
                    modifier = Modifier
                        .cursor(Cursor.Pointer)
                        .onClick {
                            if (currentPage < totalPages - 1) {
                                onPageChange(currentPage + 1)
                            }
                        }
                )
            }
        }
    }
}

